/**
 * Logique pure de la disponibilité des médecins (sans React ni appel HTTP) :
 * grille horaire, dates `yyyy-MM-dd`, regroupements d'affichage, aperçu
 * d'impact à demander et droits d'édition. Testée dans `availability.test.ts`.
 */
import { isAdmin, isDoctor, routePaths } from "../constants/roles";
import {
  Absence,
  AbsenceRequest,
  AvailabilityImpactRequest,
  DAYS_OF_WEEK,
  DayOfWeek,
  ImpactedAppointment,
  Slot,
  WeeklyAvailability,
  WeeklyAvailabilityRequest,
} from "../services/availability/types";

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

// ── Heures ──────────────────────────────────────────────────────────────

/** `"08:30"` (ou `"08:30:00"`) → 510. */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

/** 510 → `"08:30"`. */
export const minutesToTime = (totalMinutes: number): string => {
  const hours = String(Math.floor(totalMinutes / MINUTES_PER_HOUR)).padStart(2, "0");
  const minutes = String(totalMinutes % MINUTES_PER_HOUR).padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Heures de la grille, de `00:00` à la dernière borne de la journée
 * (`23:45` pour 15 min) : le serveur refuse les bornes hors grille (D9).
 */
export const buildTimeGrid = (slotMinutes: number): string[] => {
  const grid: string[] = [];
  for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += slotMinutes) {
    grid.push(minutesToTime(minutes));
  }
  return grid;
};

/** Une plage est valide quand sa fin suit strictement son début. */
export const isTimeRangeValid = (startTime: string, endTime: string): boolean =>
  Boolean(startTime && endTime) && timeToMinutes(endTime) > timeToMinutes(startTime);

// ── Dates `yyyy-MM-dd` ──────────────────────────────────────────────────

/** Ajoute `days` jours à une date `yyyy-MM-dd`, sans dépendre du fuseau. */
export const addDays = (dateKey: string, days: number): string => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
};

/** `"2026-11-04"` → « mercredi 4 novembre 2026 » (options `Intl` surchargeables). */
export const formatDateKey = (
  dateKey: string,
  options: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
): string => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("fr-FR", options);
};

// ── Créneaux libres ─────────────────────────────────────────────────────

export type SlotPeriod = "morning" | "afternoon";

/** Les créneaux qui commencent à partir de midi sont de l'après-midi. */
const AFTERNOON_START_MINUTES = 12 * MINUTES_PER_HOUR;

export const SLOT_PERIODS: { value: SlotPeriod; label: string }[] = [
  { value: "morning", label: "Matin" },
  { value: "afternoon", label: "Après-midi" },
];

const byStartTime = (left: { startTime: string }, right: { startTime: string }) =>
  left.startTime.localeCompare(right.startTime);

/** Répartit les créneaux en matin / après-midi, chacun trié par heure. */
export const groupSlotsByPeriod = (slots: Slot[]): Record<SlotPeriod, Slot[]> => {
  const groups: Record<SlotPeriod, Slot[]> = { morning: [], afternoon: [] };
  [...slots].sort(byStartTime).forEach((slot) => {
    const period = timeToMinutes(slot.startTime) < AFTERNOON_START_MINUTES ? "morning" : "afternoon";
    groups[period].push(slot);
  });
  return groups;
};

/** Premier créneau libre, dans l'ordre chronologique, ou `null`. */
export const firstSlot = (slots: Slot[]): Slot | null =>
  [...slots].sort((left, right) => left.date.localeCompare(right.date) || byStartTime(left, right))[0] ??
  null;

// ── Semaine type et absences ────────────────────────────────────────────

/** Plages rangées par jour (les sept jours sont présents), triées par heure. */
export const groupWeeklyByDay = (
  weekly: WeeklyAvailability[]
): Record<DayOfWeek, WeeklyAvailability[]> => {
  const groups = {} as Record<DayOfWeek, WeeklyAvailability[]>;
  DAYS_OF_WEEK.forEach((day) => {
    groups[day.value] = [];
  });
  [...weekly].sort(byStartTime).forEach((range) => groups[range.dayOfWeek]?.push(range));
  return groups;
};

/**
 * Absences à venir ou en cours (de la plus proche à la plus lointaine) et
 * absences passées (de la plus récente à la plus ancienne).
 */
export const splitAbsences = (
  absences: Absence[],
  today: string
): { upcoming: Absence[]; past: Absence[] } => {
  const upcoming = absences
    .filter((absence) => absence.endDate >= today)
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
  const past = absences
    .filter((absence) => absence.endDate < today)
    .sort((left, right) => right.startDate.localeCompare(left.startDate));
  return { upcoming, past };
};

/** Une absence est valide quand elle ne finit pas avant de commencer. */
export const isDateRangeValid = (startDate: string, endDate: string): boolean =>
  Boolean(startDate && endDate) && endDate >= startDate;

// ── Changements et aperçu d'impact ──────────────────────────────────────

/** Une modification de la disponibilité, telle que l'écran la demande. */
export type AvailabilityChange =
  | { kind: "createWeekly"; request: WeeklyAvailabilityRequest }
  | { kind: "updateWeekly"; weeklyId: number; request: WeeklyAvailabilityRequest }
  | { kind: "deleteWeekly"; weeklyId: number }
  | { kind: "createAbsence"; request: AbsenceRequest }
  | { kind: "updateAbsence"; absenceId: number; request: AbsenceRequest }
  | { kind: "deleteAbsence"; absenceId: number };

/**
 * Corps de l'aperçu d'impact pour `change`, ou `null` quand le changement ne
 * peut retirer aucune disponibilité (suppression d'une absence).
 */
export const impactRequestFor = (change: AvailabilityChange): AvailabilityImpactRequest | null => {
  switch (change.kind) {
    case "createWeekly":
      return { weekly: change.request };
    case "updateWeekly":
      return { replacedWeeklyId: change.weeklyId, weekly: change.request };
    case "deleteWeekly":
      return { replacedWeeklyId: change.weeklyId };
    case "createAbsence":
      return { absence: change.request };
    case "updateAbsence":
      return { replacedAbsenceId: change.absenceId, absence: change.request };
    case "deleteAbsence":
      return null;
  }
};

/** « Prénom Nom » du patient d'un RDV touché, ou un repli lisible. */
export const impactedPatientName = (appointment: ImpactedAppointment): string =>
  [appointment.patientFirstName, appointment.patientLastName].filter(Boolean).join(" ") ||
  `Patient n° ${appointment.patientId}`;

// ── Droits (D6) ─────────────────────────────────────────────────────────

/** Un médecin qui n'est pas administrateur ne voit que sa propre disponibilité. */
export const canChooseDoctor = (user: unknown): boolean => isAdmin(user) || !isDoctor(user);

/** Administrateur : tout médecin ; médecin : lui-même ; secrétaire : lecture seule. */
export const canEditAvailability = (
  user: { id?: number | null } | null | undefined,
  doctorId: number | null | undefined
): boolean => {
  if (doctorId == null) return false;
  if (isAdmin(user)) return true;
  return isDoctor(user) && user?.id === doctorId;
};

/** Paramètre d'URL du médecin affiché sur la page de disponibilité (`/disponibilites?medecin=12`). */
export const AVAILABILITY_DOCTOR_PARAM = "medecin";

/** Lien vers la disponibilité d'un médecin (écran Médecins). */
export const availabilityPathFor = (doctorId: number): string =>
  `${routePaths.AVAILABILITY}?${AVAILABILITY_DOCTOR_PARAM}=${doctorId}`;
